import { ref } from '@nuxtjs/composition-api';
import { escapeParam } from '~/composables/product/useSearchAPIParam';
import {
  GROUP_HT,
  GROUP_HN,
  WORD_TO_AVOID,
  PRODUCT_LIST_MAP,
  PRODUCTION_YEAR_THRESHOLD,
  FIRST_SIZE,
  PRODUCT_SEARCH_PARAM,
} from '~/constants';
import { ProductSearch } from '~/types/modules/contents';
import { ProductSearchSortTarget, ProductSearchSortOrder, ArticleType } from '~/enums';

export const useSearchProductsParam = () => {
  // 在庫の有無
  const hasStockQuery = ref(false);

  /**
   * 絞り込み
   * @param param 絞り込み条件
   * @param hasStock 在庫の有無
   * @param isDisplay 検索除外フラグの有無
   * @returns {string}
   */
  const createQuery = async (param: ProductSearch, isDisplay: boolean, hasStock?: boolean) => {
    let q = `mst:product AND NOT product_type_s: プリムール ${isDisplay ? 'NOT display_flag_i: 0' : ''}`;

    const {
      stock,
      brand_id,
      native_areas_id,
      size,
      color_id,
      price_to,
      price_from,
      producer_id,
      product_name,
      product_code,
      product_labels,
      vintage_code_to,
      vintage_code_from,
      producing_area_ids,
      product_category_id,
      free_word,
    } = param;

    const params = {
      brand_id,
      color_id,
      native_areas_id,
      producer_id,
      product_labels,
      product_category_id,
      size,
    };

    const validateKey = (key: string): key is keyof typeof PRODUCT_LIST_MAP => {
      switch (key) {
        case 'brand_id':
        case 'color_id':
        case 'native_areas_id':
        case 'producer_id':
        case 'product_category_id':
        case 'size':
          return true;
        default:
          return false;
      }
    };

    Object.entries(params).forEach(([key, value]) => {
      if (!validateKey(key) || !value) return;

      q += ` AND ${PRODUCT_LIST_MAP[key]}: ${value}`;
    });

    // 金額
    if (price_to || price_from) q += ` AND product_variant_price_im: [${price_from || '*'} TO ${price_to || '*'}]`;
    // 商品名
    if (product_name) q += ` AND product_name_s: *${product_name}*`;
    // 商品コード
    if (product_code) q += ` AND product_code_s: *${product_code}*`;
    // 商品ラベル
    if (product_labels)
      q += ` AND product_label_sm: (${product_labels} OR ${product_labels}_* OR *_${product_labels}_* OR *_${product_labels})`;
    // ヴィンテージ
    if (vintage_code_from && vintage_code_to) {
      const length = vintage_code_to - vintage_code_from + 1;
      const array = new Array(length).fill(+vintage_code_from);
      const years = array.map((item, index) => item + index);

      q += ` AND vintage_code_list_sm: (${years.join(' OR ')})`;
    }
    // 生産地
    if (producing_area_ids?.length) {
      q += ` AND producing_area_sm: (${producing_area_ids.join(' OR ')})`;
    }
    // 自由検索
    if (free_word) {
      const isIncluded = free_word.includes(WORD_TO_AVOID);

      if (isIncluded) q += ' AND shipping_fee_discount_flag_i: 1';
      const filteredWord = free_word.replace(WORD_TO_AVOID, '');

      const escapedWord = escapeParam(filteredWord);

      const htQuery = GROUP_HT.map((param) => `${param}: "${escapedWord}"`).join('^5 OR ');
      const hnQuery = GROUP_HN.map((param) => `${param}: "${escapedWord}"`).join(' OR ');

      q += ` AND (${htQuery}${hnQuery})`;
    }

    /**
     * 在庫の有無
     * ・ブラウザバック対応で保持してある値（hasStock）がある場合は一番優先して判定する
     * ・次点でCMSから返ってきている値（stock）を判定する
     * ・いずれの値もない場合は、デフォルトで在庫なしにする
     */
    const isRequiredStockQuery = hasStock !== undefined ? hasStock : stock ? stock === 'present' : false;
    if (isRequiredStockQuery) {
      q += ' AND has_stock_flag_i: 1';
      hasStockQuery.value = true;
    } else {
      hasStockQuery.value = false;
    }

    return {
      q,
      hasStockQuery,
    };
  };

  /**
   * ソート
   * @param target 種類
   * @param order 昇順・降順
   * @param resParam CMSのレスポンスのパラメータ
   * @param articleType 記事のタイプ
   * @returns {string}
   */
  const createSortParam = (target: number | null, order: string | null, resParam: string, articleType: string) => {
    // 生産者特集詳細画面のみ
    const hasStockSort =
      !hasStockQuery.value && articleType === ArticleType.Producers
        ? `${PRODUCT_SEARCH_PARAM.STOCK} ${ProductSearchSortOrder.DESC}, `
        : '';

    // デフォルト：おすすめ順（おすすめフラグ降順 + 生産者重み昇順 + 価格昇順 + スコア降順）
    const DEFAULT_SORT = 'recommend_flag_i asc,producer_weight_i desc,lowest_price_general_i asc,score desc';

    if (target === ProductSearchSortTarget.PRICE && order === ProductSearchSortOrder.ASC) {
      return `${hasStockSort}lowest_price_general_i ${order}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.PRICE && order === ProductSearchSortOrder.DESC) {
      return `${hasStockSort}highest_price_general_i ${order}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.VINTAGE && order === ProductSearchSortOrder.ASC) {
      return `${hasStockSort}if(exists(earliest_produced_year_i),earliest_produced_year_i,${PRODUCTION_YEAR_THRESHOLD}) ${order}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.VINTAGE && order === ProductSearchSortOrder.DESC) {
      return `${hasStockSort}latest_produced_year_i ${order}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.REVIEW && order === ProductSearchSortOrder.DESC) {
      return `${hasStockSort}sort_r_rating_r ${order}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.MEDIA && order === ProductSearchSortOrder.DESC) {
      return `${hasStockSort}vintage_media_best_score_i ${order}, vintage_media_display_order_i ${ProductSearchSortOrder.ASC}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.SIZE) {
      return `${hasStockSort}abs(sub(sort_size_amount_i, ${FIRST_SIZE})) ${order}, ${DEFAULT_SORT}`;
    }

    if (target === ProductSearchSortTarget.ARRIVAL && order === ProductSearchSortOrder.DESC) {
      return `${hasStockSort}sort_newer_d ${order}, ${DEFAULT_SORT}`;
    }

    // おすすめ順
    return !resParam ? DEFAULT_SORT : `${hasStockSort}${resParam}, ${DEFAULT_SORT}`;
  };

  return {
    createQuery,
    createSortParam,
  };
};
